import type { PipelineRun, PipelineRunFilter } from '@clm/shared-types';

import { ApiError } from '../../api/errors';
import { classifyApiError, type ApiProblemKind } from '../../api/problem';

/**
 * Shared bits for the Pipeline page's **Runs** section — CLM-83.
 *
 * Role lists are *display hints* (`mayAttempt`, `permissions.ts`): the API's
 * action check is the gate (NFR-06), and an identity that names no role sees
 * every control enabled and the API's 403 (D-82-1).
 */

/** The roles the gateway grants `pipeline:request` to. */
export const REQUEST_ROLES: readonly string[] = ['contributor', 'admin'];
/** The roles the gateway grants `pipeline:approve` to (D-83-2). */
export const APPROVE_ROLES: readonly string[] = ['admin'];

/** The active card treats a heartbeat older than this as stale. */
export const STALE_HEARTBEAT_MS = 2 * 60 * 1000;
/** The active card's poll interval. */
export const ACTIVE_POLL_MS = 5_000;

export interface RunProblem {
  readonly kind: ApiProblemKind;
  readonly field_errors: readonly { readonly path: string; readonly message: string }[];
  readonly correlation_id: string | null;
}

export function toRunProblem(error: unknown): RunProblem {
  const body = error instanceof ApiError ? (error.body as { field_errors?: unknown } | null) : null;
  const fieldErrors = Array.isArray(body?.field_errors)
    ? (body.field_errors as { path?: unknown; message?: unknown }[])
        .filter((entry) => typeof entry.path === 'string' && typeof entry.message === 'string')
        .map((entry) => ({ path: entry.path as string, message: entry.message as string }))
    : [];
  return {
    kind: classifyApiError(error),
    field_errors: fieldErrors,
    correlation_id: error instanceof ApiError ? error.correlation_id : null,
  };
}

/** The headline for a failed control action; the 409 on request / run-now is the one-slot rule (D-83-5). */
export function runProblemHead(problem: RunProblem, forbiddenCopy: string): string {
  switch (problem.kind) {
    case 'conflict':
      return problem.field_errors.some((entry) => entry.message.startsWith('run_already_pending'))
        ? 'A run is already pending'
        : 'The API refused this — the run is no longer in that state';
    case 'not_found':
      return 'This run is no longer visible — reload';
    case 'forbidden':
      return forbiddenCopy;
    default:
      return 'The request failed';
  }
}

/** One line describing what a run covers, for the pending list and history. */
export function summariseFilter(filter: PipelineRunFilter): string {
  const parts: string[] = [`limit ${filter.limit}`];
  if (filter.contract_type !== undefined) parts.push(filter.contract_type);
  if (filter.contract_key !== undefined) parts.push(filter.contract_key);
  if (filter.tags !== undefined && filter.tags.length > 0) parts.push(`tags: ${filter.tags.join(', ')}`);
  return parts.join(' · ');
}

export function isTerminal(run: Pick<PipelineRun, 'status'>): boolean {
  return run.status === 'completed' || run.status === 'failed' || run.status === 'cancelled';
}

/** Cut a long error summary for a table cell; the full text rides in `title`. */
export function truncate(text: string, max = 80): string {
  return text.length <= max ? text : `${text.slice(0, max - 1)}…`;
}
